import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated } from '@react-spring/web';
import styled, { keyframes } from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CornerBorderedBox } from '@sorare/core/src/atoms/ui/CornerBorderedBox';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';

import type { StreakRewardAnimated_anyRewardConfigInterface } from './__generated__/index.graphql';

const rewardScaleIn = keyframes`
  0% {
    bottom: 100%;
    transform: translateX(-50%) scale(0);
  }
  100% {
    bottom: 15%;
    transform: translateX(-50%) scale(1);
  }
`;
const ParentBox = styled(CornerBorderedBox)`
  width: calc(var(--unit) * 11);
  height: calc(var(--unit) * 11);
  padding: 3px;
`;
const ChildBox = styled(CornerBorderedBox)`
  height: 100%;
`;
const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  position: relative;
  width: 100%;
  height: 100%;
  background: radial-gradient(var(--c-black), transparent 80%);
  padding: var(--half-unit);
`;
const AnimatedReward = styled(animated.span)`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) scale(0);
  animation: 500ms ${rewardScaleIn} calc(var(--delay) + 2500ms) ease-out
    forwards;
`;

type Props = {
  anyRewardConfig: StreakRewardAnimated_anyRewardConfigInterface;
  delay?: number;
};
export const StreakRewardAnimated = ({ anyRewardConfig, delay = 0 }: Props) => {
  if (!anyRewardConfig) {
    return null;
  }

  return (
    <ParentBox
      cornerColor="rgba(255, 78, 34, 0.4)"
      borderWidth={1}
      cornerSize={15}
      delay={delay}
      scaleIn
    >
      <ChildBox
        cornerColor="rgba(255, 78, 34, 0.9)"
        borderWidth={2}
        cornerSize={15}
        delay={delay}
        scaleIn
      >
        <Wrapper gap={0} center>
          <AnimatedReward>
            <AnyRewardConfig
              variant={Variant.CHIP}
              anyRewardConfig={anyRewardConfig}
            />
          </AnimatedReward>
        </Wrapper>
      </ChildBox>
    </ParentBox>
  );
};

StreakRewardAnimated.fragments = {
  anyRewardConfigInterface: gql`
    fragment StreakRewardAnimated_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ...AnyRewardConfig_anyRewardConfigInterface
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<StreakRewardAnimated_anyRewardConfigInterface>,
};
